import { all, call, put, select, takeLatest } from "redux-saga/effects";
import { request, httpService } from "@app/services";

import * as endpoints from "./endpoints";
import {
  login,
  loginSuccess,
  loginFailed,
  signUp,
  signUpSuccess,
  signUpFailed,
  forgotPassword,
  forgotPasswordSuccess,
  forgotPasswordFailed,
  resetPassword,
  resetPasswordSuccess,
  resetPasswordFailed,
  verifyOtp,
  verifyOtpSuccess,
  verifyOtpFailed,
} from "./slice";
import { IRequestFailed } from "./types";

import { prepareUserSessionSaga } from "../bootstrap";
import { setLogin, setRole } from "../common";
import { selectLoginState } from "./selectors";

//---------------------LoginInSaga-----------------------

function* loginSaga(action: ReturnType<typeof login>): any {
  try {
    const { phoneNumber, email } = action.payload;

    const cleaned = phoneNumber.replace(/\D/g, "");

const formattedNumber =
  cleaned.length === 12 && cleaned.startsWith("91")
    ? `+${cleaned}`
    : cleaned.length === 10
      ? `+91${cleaned}`
      : null;

    const { data, otp } = yield call(request.post, endpoints.login, {
      number: formattedNumber,
      email: email,
    });

    const formattedData = {
      orderId: otp?.orderId,
      number: formattedNumber,
      email: email,
    };
    yield put(loginSuccess(formattedData));
  } catch (error) {
    yield put(loginFailed(error as IRequestFailed));
  }
}

//---------------------Verify With Otp Saga-----------------------

function* verifyOtpSaga(action: ReturnType<typeof verifyOtp>): any {
  try {
    const otp = action.payload;
    const loginData = yield select(selectLoginState);

    const responseData = yield call(request.post, endpoints.verifyOtp, {
      number: loginData?.data?.number,
      otp: otp,
      orderId: loginData?.data?.orderId,
      email: loginData?.data?.email,
    });

    const tokenObject = responseData?.token;
    const jwt = tokenObject?.token;
    const userData = tokenObject?.data;
    const isSuccess = tokenObject?.status;

    if (isSuccess && jwt) {
      httpService.setToken(jwt);

      yield put(setRole(userData?.role));
      yield put(
        verifyOtpSuccess({ message: responseData?.message, ...userData }),
      );
      yield put(setLogin({ ...userData, token: jwt }));

      yield call(prepareUserSessionSaga);
    } else {
      throw new Error("OTP verification failed");
    }
  } catch (error) {
    yield put(verifyOtpFailed(error as IRequestFailed));
  }
}

//--------------------SignUpSaga-------------------------

function* signUpSaga(action: ReturnType<typeof signUp>): any {
  try {
    const { firstName, lastName, email, password, userType } = action.payload;
    const { data } = yield call(request.post, endpoints.signUp, {
      firstName,
      lastName,
      email,
      password,
      userType,
    });
    yield put(signUpSuccess(data));
  } catch (error: any) {
    yield put(signUpFailed(error));
  }
}

function* forgotPasswordSaga(action: ReturnType<typeof forgotPassword>): any {
  try {
    const { email } = action.payload;
    const { data } = yield call(request.post, endpoints.forgotPassword, {
      email,
    });
    yield put(forgotPasswordSuccess(data));
  } catch (error: any) {
    yield put(forgotPasswordFailed(error));
  }
}

function* resetPasswordSaga(action: ReturnType<typeof resetPassword>): any {
  try {
    const { password, token } = action.payload;
    const { data } = yield call(
      request.put,
      endpoints.resetPassword,
      {
        password,
      },
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      },
    );

    yield put(resetPasswordSuccess(data));
  } catch (error: any) {
    yield put(resetPasswordFailed(error));
  }
}

function* authSagas() {
  yield all([
    takeLatest(login, loginSaga),
    takeLatest(signUp, signUpSaga),
    takeLatest(forgotPassword, forgotPasswordSaga),
    takeLatest(resetPassword, resetPasswordSaga),
    takeLatest(verifyOtp, verifyOtpSaga),
  ]);
}

export { authSagas };
